// Preset definitions + control option lists for AI Song Prompt Studio.
// Preset NAMES are descriptive "recipes" (sound + structure), never finished song titles.
// No preset supplies a fixed song title or a reserved lyric hook by default — the chorusHook
// field is intentionally left blank so the user (or GPT-6) creates a fresh, original hook/title.

export const VOICE_TYPES = [
  "bass", "baritone", "tenor", "alto", "contralto",
  "mezzo-soprano", "soprano", "gravelly", "warm", "airy", "powerful",
];

export const VOCALISTS = ["male", "female", "mixed group", "none"];

export const DELIVERIES = ["sung", "rapped", "spoken", "chanted", "mixed"];

export const PRIMARY_GENRES = [
  "Country", "Rap / Hip-Hop", "Blues", "Heavy Metal", "Rock", "Gospel", "Soul",
  "R&B", "Pop", "Folk", "Bluegrass", "Cinematic", "EDM", "Jazz", "Reggae",
  "Latin", "Alternative", "Instrumental", "Synthwave", "Orchestral",
];

export const MOODS = [
  "Hopeful", "Reflective", "Joyful", "Dark", "Romantic", "Defiant", "Reverent",
  "Triumphant", "Nostalgic", "Funny", "Gritty", "Haunting", "Calm", "Angry",
];

export const TEMPOS = [
  "Slow / ballad", "Moderate", "Upbeat", "Fast", "Very fast",
  "Slow build to fast", "Acoustic opening to double time",
];

export const LANGUAGES = [
  "English", "Spanish", "French", "German", "Portuguese", "Italian", "Japanese", "Latin",
];

export const SONG_LENGTHS = [
  "Short (about 2 minutes)", "Standard (about 3 minutes)", "Long (about 4 minutes)",
];

export const SONG_STRUCTURES = [
  "Verse - Chorus - Verse - Chorus - Bridge - Final Chorus",
  "Intro - 2 Verses - Chorus - 2 Verses - Final Chorus",
  "Intro - Verse - Chorus - Rap Break - Chorus - Outro",
  "Acoustic 2 Verses - Chorus - Heavy Breakdown - Final Chorus",
  "Custom: describe in chat",
];

export const EMPTY_SELECTIONS = {
  primaryGenre: "", secondaryGenre: "", mood: "", tempo: "",
  instruments: "", productionSound: "", songTopic: "", chorusHook: "",
  language: "English", songLength: "Standard (about 3 minutes)", songStructure: "", transitionPoint: "",
  cleanExplicit: "Clean", finalChorusDuet: false,
  openingVocalist: "", openingVoiceType: "", laterVocalist: "", laterVoiceType: "",
  delivery: "", customDirectives: "",
};

function base(overrides) {
  // chorusHook deliberately omitted -> stays "" so no reserved hook is ever pre-filled.
  return { ...EMPTY_SELECTIONS, language: "English", songLength: "Standard (about 3 minutes)", ...overrides };
}

export const PRESETS = [
  {
    id: "rap-to-soul-duet",
    name: "Rap to Soul Duet Story Song",
    subtitle: "Rap / Hip-Hop × Soul · duet",
    chatPrefill:
      "Begin with two thoughtful rap verses by a mature male storyteller with a deep natural voice. " +
      "After verse two, a deep-voiced female contralto responds and finishes the story. " +
      "Finish with a hopeful duet chorus. Invent a fresh, original title and hook.",
    selections: base({
      primaryGenre: "Rap / Hip-Hop", secondaryGenre: "Soul", mood: "Reflective",
      tempo: "Slow build to fast",
      instruments: "Piano, warm bass, restrained trap drums, strings",
      productionSound: "Intimate rap opening; soulful, wide finale",
      songTopic: "Two people reflect on growing up, loss, hard work, mistakes, resilience, and finding meaning in life.",
      songStructure: "Intro - 2 Verses - Chorus - 2 Verses - Final Chorus",
      transitionPoint: "Halfway through", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "bass",
      laterVocalist: "female", laterVoiceType: "contralto", delivery: "mixed",
      customDirectives: "Opening: two rapped verses by a mature deep male voice. Later/second vocalist delivery: sung. Create an original chorus hook; do not imitate any named artist.",
    }),
  },
  {
    id: "country-rap-work-anthem",
    name: "Country Rap Work Anthem",
    subtitle: "Country × Rap · gritty",
    selections: base({
      primaryGenre: "Country", secondaryGenre: "Rap / Hip-Hop", mood: "Gritty", tempo: "Upbeat",
      instruments: "Slide guitar, kick drum, bass, handclaps, harmonica",
      productionSound: "Punched-up live band and work-crew chorus",
      songTopic: "A hard-working crew starts before dawn and takes pride in the work they leave behind.",
      songStructure: "Intro - Verse - Chorus - Rap Break - Chorus - Outro",
      transitionPoint: "At bridge", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "baritone",
      laterVocalist: "mixed group", laterVoiceType: "powerful", delivery: "mixed",
      customDirectives: "Later/second vocalist (work-crew group) delivery: chanted. Create an original, singable chorus hook.",
    }),
  },
  {
    id: "gospel-rock-resurrection-duet",
    name: "Gospel Rock Resurrection Duet",
    subtitle: "Gospel × Rock · triumphant",
    selections: base({
      primaryGenre: "Gospel", secondaryGenre: "Rock", mood: "Triumphant", tempo: "Slow build to fast",
      instruments: "Church piano, organ, choir, electric guitar, drums",
      productionSound: "Reverent beginning, victorious choir finale",
      songTopic: "Rising up with gratitude after a long season of sorrow; renewed strength, light and hope.",
      songStructure: "Verse - Chorus - Verse - Chorus - Bridge - Final Chorus",
      transitionPoint: "At bridge", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "bass",
      laterVocalist: "female", laterVoiceType: "alto", delivery: "sung",
      customDirectives: "Create an original, uplifting chorus hook.",
    }),
  },
  {
    id: "country-metal-party-breakdown",
    name: "Country Metal Party Breakdown",
    subtitle: "Country × Metal · joyful",
    selections: base({
      primaryGenre: "Country", secondaryGenre: "Heavy Metal", mood: "Joyful", tempo: "Fast",
      instruments: "Banjo, harmonica, distorted guitar, slap bass, stomps, drums",
      productionSound: "Danceable groove with a metal-rap breakdown",
      songTopic: "A carefree day off lets friends leave work stress behind and cut loose together.",
      songStructure: "Intro - Verse - Chorus - Rap Break - Chorus - Outro",
      transitionPoint: "After second verse", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "baritone",
      laterVocalist: "mixed group", laterVoiceType: "powerful", delivery: "mixed",
      customDirectives: "Later/second vocalist (group) delivery: chanted. Create an original, fun chorus hook.",
    }),
  },
  {
    id: "acoustic-to-metal-story-song",
    name: "Acoustic to Metal Story Song",
    subtitle: "Blues × Metal · nostalgic",
    selections: base({
      primaryGenre: "Blues", secondaryGenre: "Heavy Metal", mood: "Nostalgic",
      tempo: "Acoustic opening to double time",
      instruments: "Banjo, harmonica, spoons, washboard, tambourine, distorted guitar",
      productionSound: "Organic porch sound, fast rap-metal turn",
      songTopic: "A couple remembers and renews their promises at a cherished place from their youth.",
      songStructure: "Acoustic 2 Verses - Chorus - Heavy Breakdown - Final Chorus",
      transitionPoint: "After first chorus", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "baritone",
      laterVocalist: "female", laterVoiceType: "alto", delivery: "sung",
      customDirectives: "Create an original chorus hook.",
    }),
  },
  {
    id: "blues-metal-rap-confession",
    name: "Blues Metal Rap Confession",
    subtitle: "Metal × Blues · defiant",
    selections: base({
      primaryGenre: "Heavy Metal", secondaryGenre: "Blues", mood: "Defiant", tempo: "Very fast",
      instruments: "Resonator guitar, double-kick drums, bass, aggressive guitar",
      productionSound: "Raw blues confession explodes into rapid metal rap",
      songTopic: "A person owns their past mistakes and makes things right through decisive action.",
      songStructure: "Intro - Verse - Chorus - Rap Break - Chorus - Outro",
      transitionPoint: "After first verse", finalChorusDuet: false,
      openingVocalist: "male", openingVoiceType: "gravelly",
      laterVocalist: "none", laterVoiceType: "", delivery: "rapped",
      customDirectives: "Create an original, hard-hitting chorus hook.",
    }),
  },
  {
    id: "cinematic-gospel-praise-duet",
    name: "Cinematic Gospel Praise Duet",
    subtitle: "Gospel × Cinematic · reverent",
    selections: base({
      primaryGenre: "Gospel", secondaryGenre: "Cinematic", mood: "Reverent", tempo: "Slow build to fast",
      instruments: "Acoustic guitar, flute, frame drums, toms, strings, guitar",
      productionSound: "Celestial scale; reverent and grounded",
      songTopic: "Praise for the beauty of the heavens, stars, waters, and all of creation.",
      songStructure: "Verse - Chorus - Verse - Chorus - Bridge - Final Chorus",
      transitionPoint: "At bridge", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "bass",
      laterVocalist: "female", laterVoiceType: "alto", delivery: "mixed",
      customDirectives: "Later/second vocalist delivery: sung. Create an original, reverent chorus hook.",
    }),
  },
  {
    id: "folk-metal-epic-journey-duet",
    name: "Folk Metal Epic Journey Duet",
    subtitle: "Folk × Metal · haunting",
    selections: base({
      primaryGenre: "Folk", secondaryGenre: "Heavy Metal", mood: "Haunting",
      tempo: "Acoustic opening to double time",
      instruments: "Acoustic guitar, cello, horns, war drums, heavy guitar",
      productionSound: "Intimate beginning, enormous second half",
      songTopic: "Two travelers answer the call of a hard journey home through wild mountain country.",
      songStructure: "Acoustic 2 Verses - Chorus - Heavy Breakdown - Final Chorus",
      transitionPoint: "After second verse", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "baritone",
      laterVocalist: "female", laterVoiceType: "contralto", delivery: "mixed",
      customDirectives: "Later/second vocalist delivery: sung and rapped. Create an original, epic chorus hook.",
    }),
  },
  {
    id: "country-blues-crew-anthem",
    name: "Country Blues Crew Anthem",
    subtitle: "Country × Blues · gritty",
    selections: base({
      primaryGenre: "Country", secondaryGenre: "Blues", mood: "Gritty", tempo: "Upbeat",
      instruments: "Slide guitar, bass, snare, worksite percussion, organ",
      productionSound: "Dusty live mix with a strong last chorus",
      songTopic: "A crew races the weather and the setting sun to finish a job the right way.",
      songStructure: "Verse - Chorus - Verse - Chorus - Bridge - Final Chorus",
      transitionPoint: "At bridge", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "gravelly",
      laterVocalist: "mixed group", laterVoiceType: "baritone", delivery: "sung",
      customDirectives: "Later/second vocalist (crew group) delivery: chanted. Create an original chorus hook.",
    }),
  },
  {
    id: "rock-soul-comeback-duet",
    name: "Rock Soul Comeback Duet",
    subtitle: "Rock × Soul · triumphant",
    selections: base({
      primaryGenre: "Rock", secondaryGenre: "Soul", mood: "Triumphant", tempo: "Moderate",
      instruments: "Signature low guitar motif, tribal drums, bass, strings, tambourine",
      productionSound: "Distinct character voices and a recurring musical motif",
      songTopic: "Two people face a new challenge while holding on to who they have always been.",
      songStructure: "Verse - Chorus - Verse - Chorus - Bridge - Final Chorus",
      transitionPoint: "After first verse", finalChorusDuet: true,
      openingVocalist: "male", openingVoiceType: "bass",
      laterVocalist: "female", laterVoiceType: "contralto", delivery: "mixed",
      customDirectives: "Opening delivery: rapped. Later/second vocalist delivery: sung. Create an original chorus hook.",
    }),
  },
];
